/**
 * ワイド型の目次（ADR-0025）の設定。
 * scenario.config.wideAgenda に保存し、埋め込み側（プレイヤー）と管理画面側（編集画面）で共有する。
 */

import type { AgendaItem, ScenarioNode } from '@/types/interactiveVideo'

/** 目次の1項目として保存する値。ノードはシナリオ内で不変の nodeKey で指す */
export type WideAgendaItemConfig = {
  nodeKey: string
}

/** WIDE 型の scenario.config.wideAgenda に保持する設定 */
export type WideAgendaConfig = {
  enabled?: boolean
  items?: WideAgendaItemConfig[]
}

/**
 * scenario.config から、プレイヤーに渡す「目次をオンにした動画」の一覧を作る。
 * 目次に並ぶのは各動画のインタラクティブなので、インタラクティブの無い動画・存在しない動画・アンケートの箱は除く。
 */
export function buildWideAgenda(
  scenarioConfig: unknown,
  nodes: ReadonlyMap<string, ScenarioNode>,
): AgendaItem[] | undefined {
  const raw = (scenarioConfig as { wideAgenda?: WideAgendaConfig } | null)?.wideAgenda
  if (!raw?.enabled || !Array.isArray(raw.items)) return undefined

  const seen = new Set<string>()
  const agenda: AgendaItem[] = []
  for (const item of raw.items) {
    if (typeof item?.nodeKey !== 'string' || seen.has(item.nodeKey)) continue
    const node = nodes.get(item.nodeKey)
    if (!node || node.type === 'SURVEY' || (node.hotspots ?? []).length === 0) continue
    seen.add(item.nodeKey)
    agenda.push({ nodeId: item.nodeKey })
  }
  return agenda.length > 0 ? agenda : undefined
}

type AgendaSourceNode = {
  nodeKey: string
  type: string
  buttons?: Array<{ actionConfig?: unknown }> | null
  hotspots?: Array<{ startTime: number; actionConfig?: unknown }> | null
}

/** 動画カードの nodeKey を、スタート動画から分岐ボタン・インタラクティブの行き先をたどった順に並べる（たどれないカードは後ろに元の順で付ける） */
const orderVideoNodeKeys = (nodes: readonly AgendaSourceNode[], initialNodeKey: string | null | undefined): string[] => {
  const byKey = new Map(nodes.map((node) => [node.nodeKey, node]))
  const ordered: AgendaSourceNode[] = []
  const visited = new Set<string>()
  const queue = initialNodeKey ? [initialNodeKey] : []
  while (queue.length > 0) {
    const key = queue.shift() as string
    if (visited.has(key)) continue
    visited.add(key)
    const node = byKey.get(key)
    if (!node) continue
    ordered.push(node)
    // インタラクティブは動画の中で出てくる順（表示開始の早い順）にたどる
    const hotspots = [...(node.hotspots ?? [])].sort((a, b) => a.startTime - b.startTime)
    for (const link of [...(node.buttons ?? []), ...hotspots]) {
      const target = (link.actionConfig as { targetNodeKey?: unknown } | null)?.targetNodeKey
      if (typeof target === 'string' && !visited.has(target)) queue.push(target)
    }
  }
  for (const node of nodes) if (!visited.has(node.nodeKey)) ordered.push(node)
  return ordered.filter((node) => node.type === 'VIDEO').map((node) => node.nodeKey)
}

/** 保存済みの設定で、目次をオンにしている動画の nodeKey */
const readAgendaNodeKeys = (scenarioConfig: unknown): string[] => {
  const raw = (scenarioConfig as { wideAgenda?: WideAgendaConfig } | null)?.wideAgenda
  if (!raw?.enabled || !Array.isArray(raw.items)) return []
  return raw.items.flatMap((item) => (typeof item?.nodeKey === 'string' ? [item.nodeKey] : []))
}

/** この動画で目次をオンにしているか */
export function isAgendaShownOn(scenarioConfig: unknown, nodeKey: string): boolean {
  return readAgendaNodeKeys(scenarioConfig).includes(nodeKey)
}

/**
 * 1本の動画の目次のオン／オフを切り替えた設定を返す。
 * オンにした動画を見ている間だけ、その動画のインタラクティブを目次に並べる。
 * 保存する並び順はスタート動画から分岐をたどった順。
 */
export function withAgendaShownOn(
  scenarioConfig: unknown,
  nodeKey: string,
  shown: boolean,
  nodes: readonly AgendaSourceNode[],
  initialNodeKey: string | null | undefined,
): WideAgendaConfig {
  const keys = new Set(readAgendaNodeKeys(scenarioConfig))
  if (shown) keys.add(nodeKey)
  else keys.delete(nodeKey)
  const items = orderVideoNodeKeys(nodes, initialNodeKey)
    .filter((key) => keys.has(key))
    .map((key) => ({ nodeKey: key }))
  return { enabled: items.length > 0, items }
}
